// P21 + Option 1 (2/3) — Page /profil : UX améliorée (loading states, validation, avatar)
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { useAuth } from "@/hooks/useAuth";
import { Save, Lock, User, Mail, Shield } from "lucide-react";
import { useT } from "@/lib/i18n";
import { API_BASE } from "@/lib/apiBase";
interface MeResponse {
  id: number;
  email: string;
  name: string;
  role: string;
}

async function fetchJson<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${url}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...options.headers },
    ...options,
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export default function ProfilPage() {
  const { user, refresh } = useAuth();
  const { toast } = useToast();
  const t = useT();
  const [name, setName] = useState(user?.name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [profileDirty, setProfileDirty] = useState(false);

  const { data: me } = useQuery({
    queryKey: ["me"],
    queryFn: () => fetchJson<MeResponse>(`/v1/auth/me`),
    initialData: user
      ? { id: 0, email: user.email, name: user.name, role: user.role }
      : undefined,
  });

  const updateMe = async () => {
    setSavingProfile(true);
    try {
      await fetchJson<MeResponse>(`/v1/auth/me`, {
        method: "PATCH",
        body: JSON.stringify({ name, email }),
      });
      await refresh();
      toast({ title: t("profil.success_update"), variant: "success" });
      setProfileDirty(false);
    } catch (err) {
      toast({
        title: t("profil.error_update"),
        description: (err as Error).message,
        variant: "error",
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast({ title: t("profil.password_mismatch"), variant: "warning" });
      return;
    }
    if (newPassword.length < 6) {
      toast({
        title: t("profil.password_too_short").replace("{n}", "..."),
        variant: "warning",
      });
      return;
    }
    setSavingPassword(true);
    try {
      await fetchJson(`/v1/auth/change-password`, {
        method: "POST",
        body: JSON.stringify({
          old_password: oldPassword,
          new_password: newPassword,
        }),
      });
      toast({ title: t("profil.success_password"), variant: "success" });
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast({
        title: t("profil.error_password"),
        description: (err as Error).message,
        variant: "error",
      });
    } finally {
      setSavingPassword(false);
    }
  };

  const initials = (me?.name ?? user?.name ?? "U")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-4">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary text-2xl font-bold text-primary-foreground">
          {initials}
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("profil.title")}</h1>
          <p className="text-muted-foreground">
            {me?.name ?? user?.name} · {me?.email ?? user?.email}
          </p>
        </div>
      </div>

      {/* Section 1 : Informations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Informations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 max-w-md">
            <div>
              <label className="text-xs flex items-center gap-1">
                <User className="h-3 w-3" /> {t("profil.field.name")}
              </label>
              <input
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setProfileDirty(true);
                }}
              />
            </div>
            <div>
              <label className="text-xs flex items-center gap-1">
                <Mail className="h-3 w-3" /> {t("profil.field.email")}
              </label>
              <input
                type="email"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setProfileDirty(true);
                }}
              />
            </div>
            <div>
              <label className="text-xs flex items-center gap-1">
                <Shield className="h-3 w-3" /> {t("profil.field.role")}
              </label>
              <div className="rounded-md border border-input bg-muted px-3 py-2 text-sm">
                <span className="font-mono">{me?.role ?? user?.role}</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  {t("profil.role_readonly")}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={updateMe}
                disabled={
                  savingProfile ||
                  !profileDirty ||
                  !name.trim() ||
                  !email.trim() ||
                  !email.includes("@")
                }
              >
                <Save className="mr-1 h-4 w-4" />
                {savingProfile ? t("profil.saving") : t("profil.action.save")}
              </Button>
              {profileDirty && (
                <span className="text-xs text-muted-foreground">
                  {t("profil.modifications_unsaved")}
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 2 : Mot de passe */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-4 w-4" />
            {t("profil.action.change_password")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 max-w-md">
            <div>
              <label className="text-xs">{t("profil.field.old_password")}</label>
              <input
                type="password"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                autoComplete="current-password"
                disabled={savingPassword}
              />
            </div>
            <div>
              <label className="text-xs">{t("profil.field.new_password")}</label>
              <input
                type="password"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                disabled={savingPassword}
              />
              {newPassword && newPassword.length < 6 && (
                <div className="mt-1 text-xs text-red-600">
                  Encore (6 - newPassword.length).toString()
                </div>
              )}
            </div>
            <div>
              <label className="text-xs">{t("profil.field.confirm_password")}</label>
              <input
                type="password"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                disabled={savingPassword}
              />
              {confirmPassword && confirmPassword !== newPassword && (
                <div className="mt-1 text-xs text-red-600">
                  {t("profil.password_mismatch")}
                </div>
              )}
            </div>
            <Button
              onClick={changePassword}
              disabled={
                savingPassword ||
                !oldPassword ||
                !newPassword ||
                !confirmPassword ||
                newPassword !== confirmPassword ||
                newPassword.length < 6
              }
            >
              <Lock className="mr-1 h-4 w-4" />
              {savingPassword ? t("profil.changing") : t("profil.action.change_password")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
